import { I18n } from "@assets/strings";
import React, { JSX, useEffect, useState } from "react";
import { Link } from "react-router";
import styled from "styled-components";
import emptyOrgImage from "@assets/images/empty_org.png";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useMyOrganizations } from "@domain/organization/hooks";
import { useIsMobile } from "@utils/hooks";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { RoundedPlusButton } from "@ui/components/buttons/roundedPlusButton";
import { TertiaryButton } from "@ui/components/buttons/tertiaryButton";
import { PrimaryBold, PrimaryMedium } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ChevronDownIcon } from "@ui/components/icons";
import { row, Spacer, stack } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { OrganizationEditionModal } from "@ui/components/organization/organizationEditionModal";
import { OrganizationsModal } from "@ui/components/organization/organizationsModal";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { useModalTools } from "@ui/modals/modalContext";
import { gap } from "@ui/styles/utils";

type MyOrganizationsProps = React.HTMLAttributes<HTMLDivElement>;

//TODO definir la limite de count d'abord
// Faire un map si le nombre d'organisation en limitant le nombre d'elements;
// si le nombre d'element est superieur a la limite fixer afficher le bouton voir plus;

export const MyOrganizations: React.FC<MyOrganizationsProps> = ({ ...htmlProps }) => {
	const { result: organizations, totalElements } = useMyOrganizations();
	const { trackingService } = useServices();
	const { createModal } = useModalTools();
	const isMobile = useIsMobile();
	const [opened, setOpened] = useState(!isMobile);
	const [itemHovered, setItemHovered] = useState<string | null>(null);
	const isEmpty = totalElements !== undefined && totalElements < 1;

	const userPagesCount = 3;

	useEffect(() => {
		setOpened(!isMobile);
	}, [isMobile]);

	const renderTitle = (): JSX.Element => (
		<Title onClick={() => isMobile && setOpened((o) => !o)}>
			<PrimaryMedium>{I18n.t("profile.organizations.title")}</PrimaryMedium>
			{!isEmpty && (
				<Link to={Path.NewOrganization} onClick={() => trackingService.track("Profile - add a pro page")}>
					<RoundedPlusButton />
				</Link>
			)}
			<Spacer />
			{isMobile && (
				<div>
					<StyledChevronDownIcon reversed={opened} />
				</div>
			)}
		</Title>
	);

	if (isMobile && isEmpty) {
		return null;
	}

	return (
		<Container {...htmlProps}>
			{!isMobile && renderTitle()}
			{isEmpty ? (
				<EmptyContent>
					<img src={emptyOrgImage} />
					<Link to={Path.NewOrganization}>
						<CreateButton>{I18n.t("profile.organizations.new")}</CreateButton>
					</Link>
				</EmptyContent>
			) : (
				<Organizations>
					{isMobile && renderTitle()}
					{opened && (
						<>
							{organizations.slice(0, userPagesCount).map((org) => (
								<OrganizationView
									key={org.id}
									onMouseEnter={() => setItemHovered(org.id)}
									onMouseLeave={() => setItemHovered(null)}
								>
									<Link to={Path.Organization(org)}>
										<ProfilePicture picture={org.avatar} fallbackType={org.type} size={36} />
										<Name>{org.primaryName}</Name>
									</Link>
									<KebabMenu onClick={(e) => e.preventDefault()} isVisible={isMobile ? true : itemHovered === org.id}>
										<SimpleMenuContent
											onEdit={() =>
												createModal((close) => <OrganizationEditionModal close={close} organizationId={org.id} />)
											}
										/>
									</KebabMenu>
								</OrganizationView>
							))}
							{organizations.length > userPagesCount && (
								<TertiaryButton onClick={() => createModal((close) => <OrganizationsModal close={close} />)}>
									{I18n.t("listing.more", { count: organizations.length - userPagesCount })}
								</TertiaryButton>
							)}
						</>
					)}
				</Organizations>
			)}
		</Container>
	);
};

const Container = styled.div`
	${gap(16)};
`;

const Title = styled.div`
	${row(6, "flex-start", "center")};

	> svg {
		display: block;
		cursor: pointer;
		margin-bottom: 2px;

		path {
			fill: ${({ theme }) => theme.colors.secondary};
		}
	}
`;

const Organizations = styled(RoundedBlock)`
	padding: 20px 16px;
	${gap(15)};
`;

const EmptyContent = styled(RoundedBlock)`
	${stack(0, "flex-start", "center")};
	overflow: hidden;
	> img {
		width: 101%;
		height: 140px;
	}
`;

const CreateButton = styled(PrimaryButton)`
	margin: 0px 18px 23px;
	padding: 6px 14px;
	font-size: 0.8125rem;
`;

const OrganizationView = styled.div`
	${row("S")};
	> a {
		${row("S", "flex-start", "center")};
		flex: 1;
	}
`;

const Name = styled(PrimaryBold)`
	font-size: 0.8125rem;
`;

const StyledChevronDownIcon = styled(ChevronDownIcon)`
	margin-right: 3px;
`;
